import axios from 'axios'
import { store } from './vuex-store'
import { MUTATE_LOGOUT } from './vuex-store/types'

export const TOKEN_KEY = 'token'

const api = axios.create({
  baseURL: process.env.GOCHAT_API_BASE_URL
})

api.interceptors.request.use((req) => {
  req.headers['Authorization'] = `Bearer ${localStorage.getItem(TOKEN_KEY)}`

  return req
})

api.interceptors.response.use(
  (res) => {
    if (res.data.error || res.data.errors) {
      return Promise.reject(res.data)
    }

    return res
  },
  (err) => {
    if (err.response) {
      if (err.response.status === 401) {
        store.commit(MUTATE_LOGOUT)
      }
    }

    return Promise.reject(err)
  }
)

const wsRetriesMax = 10,
  wsRetriesInterval = 10000 // 10 seconds
const wsConnsMap = {} // {[merchantId]: {[channelId]: conn}}

const getWsConn = (merchantId, channelId = null) => {
  const chId = channelId ? channelId : '*'

  if (!wsConnsMap[merchantId]) {
    wsConnsMap[merchantId] = {}
  }

  return wsConnsMap[merchantId][chId]
}

const setWsConn = (conn, merchantId, channelId = null) => {
  if (!(conn instanceof WebSocket))
    throw new TypeError('conn must be an instance of WebSocket')

  const chId = channelId ? channelId : '*'

  if (!wsConnsMap[merchantId]) {
    wsConnsMap[merchantId] = {}
  }

  wsConnsMap[merchantId][chId] = conn
}

const deleteWsConn = (merchantId, channelId = null) => {
  if (!wsConnsMap[merchantId]) return

  const chId = channelId ? channelId : '*'

  if (!wsConnsMap[merchantId][chId]) return
  if (wsConnsMap[merchantId][chId] instanceof WebSocket) {
    wsConnsMap[merchantId][chId].close()
  }

  delete wsConnsMap[merchantId][chId]
}

export function subscribeRealtimeMessages(
  merchantId,
  channelId = null,
  handler,
  retriesCount = 0
) {
  console.log('subscribeRealtimeMessages()')
  if (!merchantId) {
    throw new Error(
      'Merchant ID is required for real-time message subscription'
    )
  }

  let ws = getWsConn(merchantId, channelId)

  if (ws instanceof WebSocket) {
    console.log('already subscribed')
    return ws
  }

  // Close and delete unused WebSocket connections
  for (const mid in wsConnsMap) {
    for (const chid in wsConnsMap[mid]) {
      if (chid === channelId) continue

      if (wsConnsMap[mid][chid] instanceof WebSocket) {
        wsConnsMap[mid][chid].close()
      }

      delete wsConnsMap[mid][chid]
    }

    delete wsConnsMap[mid]
  }

  retriesCount++

  console.log(
    `Creating new WebSocket connection (${retriesCount}/${wsRetriesMax} attempts)...`
  )

  const url = new URL(process.env.GOCHAT_API_BASE_URL, window.location)
  url.protocol = 'wss:'
  url.pathname = '/v2/ws/chat'
  url.search = '?token=' + localStorage.getItem('token')
  ws = new WebSocket(url.toString())

  ws.addEventListener('open', () => {
    console.log('socket opened')
    retriesCount = 0
    ws.send(
      JSON.stringify({
        command: 'subscribe',
        data: {
          topics: [
            `WEBHOOK:MESSAGE:RECEIVED/merchant{${merchantId}}${
              channelId ? '/channel{' + channelId + '}' : ''
            }`,
            `MESSAGE:SENT/merchant{${merchantId}}${
              channelId ? '/channel{' + channelId + '}' : ''
            }`
          ]
        }
      })
    )
  })
  ws.addEventListener('close', (e) => {
    console.log('socket closed: ', e)
    deleteWsConn(merchantId, channelId)

    if (e.code !== 1000) {
      if (retriesCount >= wsRetriesMax) {
        console.log(
          `Reached max retry counts. Will retry another round in ${
            wsRetriesInterval / 1000
          } seconds.`
        )

        setTimeout(() => {
          retriesCount = 0
          subscribeRealtimeMessages(
            merchantId,
            channelId,
            handler,
            retriesCount
          )
        }, wsRetriesInterval)
        return
      }

      subscribeRealtimeMessages(merchantId, channelId, handler, retriesCount)
    }
  })
  ws.addEventListener('message', (e) => {
    switch (e.data.constructor) {
      case Blob:
        const reader = new FileReader()
        reader.onload = () => handler(JSON.parse(reader.result))
        reader.readAsText(e.data)
        return
      case String:
        return handler(JSON.parse(e.data))
    }

    console.log('Unsupported WebSocket content type: ', e.data.constructor)
  })

  setWsConn(ws, merchantId, channelId)

  return ws
}

export default api



// WEBPACK FOOTER //
// ./src/api.js